import z from 'zod';
import { zodTDateIso } from '~/lib/zod';
import { ChannelType } from '~/lib/enum';
import * as v from 'valibot';
import form from '~/components/valibot/form';

export enum NotificationType {
  // Order Status Notifications
  ORDER_SUBMITTED = 'ORDER_SUBMITTED',
  ORDER_PAID = 'ORDER_PAID',
  ORDER_DELIVERED = 'ORDER_DELIVERED',
  ORDER_QUOTED = 'ORDER_QUOTED',
  QUOTE_ACCEPTED = 'QUOTE_ACCEPTED',
  QUOTE_REJECTED = 'QUOTE_REJECTED',

  // Payment Notifications
  PAYMENT_FAILED = 'PAYMENT_FAILED',

  // Appointment Notifications
  APPOINTMENT = 'APPOINTMENT',

  // Job Notifications
  JOB_INPUT_REQUIRED = 'JOB_INPUT_REQUIRED',
  JOB_UPLOADED_TO_EDITOR = 'JOB_UPLOADED_TO_EDITOR',
  JOB_UPLOADED_FROM_EDITOR = 'JOB_UPLOADED_FROM_EDITOR',
  JOB_ACTION_REQUIRED = 'JOB_ACTION_REQUIRED',

  // Listing marketing: the heads-up 24 h before each automatic social post
  // of a Property Page V2 campaign (api+/bleenk.campaign-heads-up.ts).
  SOCIAL_POSTS = 'SOCIAL_POSTS',
}

export const NotificationTypes = Object.values(NotificationType);

export const NotificationChannelSchema = v.record(
  v.enum(NotificationType),
  v.record(v.enum(ChannelType), form.boolean),
);

export interface UserDataNotifiedTimestamps {
  notified_appointments?: TDateISO;
  /**
   * The nightly ROUTE LIST (message 3 of the automated pipeline). Holds
   * the START OF THE ROUTED DAY, not the send time, which is what makes
   * the send idempotent per provider + date: `send_email` skips when the
   * stored stamp is already >= the day being mailed, so a re-run of the
   * same evening is silent and the next day's list still goes out.
   */
  notified_route?: TDateISO;
}

export interface UserDataNotifiedBoolean {
  notified_invited?: boolean;
  notified_password?: boolean;
}

export type UserDataNotified = UserDataNotifiedTimestamps & UserDataNotifiedBoolean;

export interface UserData extends UserDataNotified {
  first?: string;
  last?: string;
  signature?: string;
  title?: string;
  password?: string;
  password_change?: boolean;
  reset?: boolean;
  google?: boolean;
  customers: Record<string, {}>;
  office?: TPhone;
  phone?: TPhone;
  provider?: string;
  onboarded?: boolean;
  accepted?: TDateISO;
  imported?: boolean;
  brand?: string;
  sms?: boolean;
  disabled?: boolean;

  vendor_expense?: boolean;
  vendor_provider?: boolean;
  vendor_admin?: boolean;

  invite_send?: boolean;
  invite_accepted?: TDateISO;

  verified?: boolean;
  verified_send?: boolean;
  verified_sent?: TDateISO;

  /**
   * PHOTOGRAPHER SET-PASSWORD LINK (.server/provider-password-link.ts).
   * `password_link_send`: a link is to be emailed (user_dirty sends it and
   * clears this). `password_link_hash`: the SHA-256 of the one live link's
   * random id (never the id itself); cleared when the link is used, replaced
   * when a new one is sent. `password_link_expires`: when the live link stops
   * working. `password_link_sent`: when the last one was emailed.
   */
  password_link_send?: boolean;
  password_link_hash?: string;
  password_link_expires?: TDateISO;
  password_link_sent?: TDateISO;

  /**
   * TEXT ORDERING: is this account's mobile really theirs (.server/sms/phone-verification.ts)?
   * Set when the 6-digit code we texted to `phone_verified_number` came back
   * (typed in, or replied by text). Verification is for that number only: a
   * changed mobile is unverified until it is confirmed again.
   */
  phone_verified_at?: TDateISO;
  phone_verified_number?: TPhone;
  /** The code in flight: its hash (never the code), the number it went to, when, and the tries so far. */
  phone_verify_hash?: string;
  phone_verify_number?: TPhone;
  phone_verify_sent_at?: TDateISO;
  phone_verify_expires_at?: TDateISO;
  phone_verify_attempts?: number;
  /** The 24-hour window the daily caps count in: when it began, codes started and tries made in it. */
  phone_verify_window_at?: TDateISO;
  phone_verify_window_starts?: number;
  phone_verify_window_tries?: number;

  /**
   * The mobile texted STOP to this company (Twilio's OptOutType=STOP) and
   * nothing may be texted to it until it texts START: twilio_send_text
   * refuses (lib/twilio.server.ts). For `sms_opt_out_number` only.
   */
  sms_opt_out_at?: TDateISO;
  sms_opt_out_number?: TPhone;

  twilio_worker_enabled?: boolean;
  twilio_worker_sid?: string;
  twilio_worker_priority?: number;

  notifications?: Partial<Record<NotificationType, Partial<Record<ChannelType, boolean>>>>;
}

export function USER_DEFAULT_DATA(): Omit<UserData, 'first' | 'last' | 'email'> {
  return {
    customers: {},
  };
}

export const InvitedUserSchema = z.object({
  invite_send: z.boolean(),
  invite_accepted: zodTDateIso().optional(),
  invite_notified: z.boolean(),
});

export const EditorUserSchema = InvitedUserSchema;
